import includes from "lodash/includes";
import xss, { IFilterXSSOptions } from "xss";

import { ehqParticipantXssOptions } from "./ehq-participant-html";

/**
 * xss only drops a script body when it finds `</script>`. An unclosed or
 * nested tag otherwise leaks the body and can leave a literal `[removed]`.
 * Strip complete tags from the inside out, then drop any unclosed remainder.
 */
const stripScriptTags = (value?: string | null): string => {
  const completeScript =
    /<script\b[^>]*>(?:(?!<script\b)[\s\S])*?<\/script\s*>/gi;
  let previous = "";
  let current = value ? String(value) : "";

  while (current !== previous) {
    previous = current;
    current = current.replace(completeScript, "");
  }

  return current.replace(/<script\b[^>]*>[\s\S]*$/gi, "");
};

const escapeEditorHtml = (html: string): string =>
  html
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/&lt;script[\s\S]*?&lt;\/script&gt;/gi, "")
    .replace(/&lt;[!/]?\w+(?:\s|&nbsp;|[^&])*?&gt;|&lt;!--[\s\S]*?--&gt;/g, "");

export const ehqWhiteListAttributes = [
  "id",
  "class",
  "style",
  "width",
  "height",
  "title",
  "role"
];

/**
 * Allowlist participant Froala HTML the same way ehqv2 renders it.
 * Mirrors Sanitize::Config::EHQ in ehqv2/config/initializers/sanitize.rb.
 */
export const stripDangerousHtml = (
  str: string,
  options?: IFilterXSSOptions
): string => {
  return xss(stripScriptTags(str), {
    ...ehqParticipantXssOptions(),
    stripIgnoreTag: true,
    stripIgnoreTagBody: ["script"],
    ...options
  });
};

/**
 * Preserves trailing spaces in HTML using plainText as source of truth.
 * Browsers collapse trailing spaces when serializing innerHTML; this ensures they
 * persist by inserting &nbsp; before the last closing tag when plainText ends with spaces.
 */
export const preserveTrailingSpacesInHtml = (
  html: string,
  plainText: string
): string => {
  const trailingMatch = plainText.match(/ +$/);
  if (!trailingMatch) return html;

  const trailingCount = trailingMatch[0].length;
  const existingNbsp = (html.match(
    /(?:&nbsp;|\u00A0)+(?=\s*(?:<\/[a-zA-Z][^>]*>\s*)*$)/
  ) || [""])[0];
  const existingCount = (existingNbsp.match(/&nbsp;|\u00A0/g) || []).length;
  if (existingCount >= trailingCount) return html;

  const toAdd = trailingCount - existingCount;
  return html.replace(
    / *((<\/[a-zA-Z][^>]*>\s*)+)$/,
    (_match, closingTags) => "\u00A0".repeat(toAdd) + closingTags.trimStart()
  );
};

/**
 * Returns the plain-text character length of HTML, counting a single \n
 * between block-level elements to match the RTE's normalized counting
 * and backend (ROR) character validation.
 * Safe for SSR: returns 0 when document is undefined or html is empty.
 *
 * SYNC: getNormalizedTextContent() in rich-text-editor/utils/textContent.ts
 * implements the same counting algorithm via Lexical AST. Changes here must
 * be mirrored there.
 */
export const getPlainTextLengthFromHtml = (html: string): number => {
  if (typeof document === "undefined" || !html) return 0;
  const div = document.createElement("div");
  div.innerHTML = html;
  return getTextWithNewlines(div).replace(/\n{2,}/g, "\n").length;
};

const BLOCK_ELEMENTS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "UL",
  "OL",
  "LI",
  "BLOCKQUOTE",
  "PRE",
  "TABLE",
  "TR"
]);

/**
 * Extracts text from a DOM tree, inserting \n between block-level
 * elements and treating empty blocks (containing only <br>) as empty strings.
 */
function getTextWithNewlines(node: globalThis.Node): string {
  const parts: string[] = [];

  node.childNodes.forEach((child) => {
    if (child.nodeType === 3 /* TEXT_NODE */) {
      const text = child.textContent ?? "";
      if (/^\s*$/.test(text) && /[\n\r]/.test(text)) return;
      parts.push(text);
    } else if (child.nodeType === 1 /* ELEMENT_NODE */) {
      const el = child as HTMLElement;

      if (BLOCK_ELEMENTS.has(el.tagName)) {
        if (parts.length > 0) parts.push("\n");

        const inner = getTextWithNewlines(el);
        const isBrOnly =
          el.childNodes.length === 1 && el.firstChild?.nodeName === "BR";
        parts.push(isBrOnly ? "" : inner);
      } else if (el.tagName === "BR") {
        parts.push("\n");
      } else {
        parts.push(getTextWithNewlines(el));
      }
    }
  });

  return parts.join("");
}

/**
 * Remove all html tags and convert into plaintext
 * @param str
 */
export const stripAllHtml = (str: string): string => {
  return xss(stripScriptTags(str), {
    whiteList: {},
    stripIgnoreTag: true,
    stripBlankChar: true,
    stripIgnoreTagBody: ["script", "style"]
  });
};

/**
 * Limited HTML kept on Ideas, Budget, and Places engagement banners.
 * Non-visible markup is removed with its contents so code-view CSS and
 * scripts cannot appear as banner text.
 */
export const sanitizeEngagementBannerHtml = (str?: string): string => {
  return xss(stripScriptTags(str ?? ""), {
    whiteList: {
      p: ehqWhiteListAttributes,
      s: ehqWhiteListAttributes,
      u: ehqWhiteListAttributes,
      em: ehqWhiteListAttributes,
      ul: ehqWhiteListAttributes,
      li: ehqWhiteListAttributes,
      ol: ehqWhiteListAttributes,
      br: ehqWhiteListAttributes,
      strong: ehqWhiteListAttributes
    },
    stripIgnoreTag: true,
    stripIgnoreTagBody: ["table", "header", "img", "video", "style", "script"],
    onTag: (tag) => {
      if (
        includes(
          [
            "img",
            "area",
            "base",
            "col",
            "embed",
            "hr",
            "input",
            "link",
            "meta",
            "param",
            "source",
            "track",
            "wbr"
          ],
          tag
        )
      )
        return "";
    }
  });
};

export const stripEditorHtml = (str: string): string => {
  return xss(stripScriptTags(str), {
    whiteList: {
      p: ehqWhiteListAttributes,
      b: ehqWhiteListAttributes,
      i: ehqWhiteListAttributes,
      u: ehqWhiteListAttributes,
      ul: ehqWhiteListAttributes,
      li: ehqWhiteListAttributes,
      br: ehqWhiteListAttributes,
      span: ehqWhiteListAttributes,
      strong: ehqWhiteListAttributes,
      a: [...ehqWhiteListAttributes, "href", "target", "rel"]
    },
    stripIgnoreTag: true,
    stripIgnoreTagBody: ["script", "table", "header", "img", "video"],
    escapeHtml: escapeEditorHtml,
    onTag: (tag) => {
      if (
        includes(
          [
            "img",
            "area",
            "base",
            "col",
            "embed",
            "hr",
            "input",
            "link",
            "meta",
            "param",
            "source",
            "track",
            "wbr"
          ],
          tag
        )
      )
        return "";
    }
  });
};
